import React, { useState, useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { Button } from "@shared/ui/button";
import { Eye } from "lucide-react";
import { useConsumerRequests } from "../../../../../requests/hook";
import { useAllAccounts } from "../../../../../shared/hook";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { RequestDetailsModal } from "../../../../../requests/details/components/RequestDetailsModal";
import { PAGE_SIZE } from '@shared/utils/constants';
import { useTranslation } from "react-i18next";

export default function Complaints() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const limit = PAGE_SIZE || 10;
  const { consumerId } = getLoginDataFromStorage() || {};
  const remoteUtilityId = getRemoteUtilityId();

  const { data: allAccounts } = useAllAccounts({
    page: 1,
    limit: 1000,
    active_consumers: 1,
    remote_utility_id: remoteUtilityId,
    is_sub_account: 1,
    consumer_id: consumerId,
  });

  const allConsumerIds = useMemo(() => {
    const subIds = allAccounts?.results?.map((acc: any) => acc.id) || [];
    return subIds;
  }, [allAccounts, consumerId]);
  
  const [selectedRequest, setSelectedRequest] = useState<any>(null);

  const { data: requestData, isLoading } = useConsumerRequests({
    page,
    limit,
    consumer_id: allConsumerIds,
    remote_utility_id: remoteUtilityId,
    request_type: 'Complaint',
  });

  const columns: any[] = useMemo(() => [
    {
      key: "consumer",
      header: t("Account"),
      render: (val: any, row: any) => (
        <div className="flex items-center space-x-3">
          <div className="w-2 h-2 rounded-full bg-blue-600"></div>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-900">{row?.consumer?.consumerNo || t("N/A")}</span>
            <span className="text-xs text-gray-500">{row?.consumer?.consumerName || t("N/A")}</span>
          </div>
        </div>
      )
    },
    { header: t("Request ID"), key:'requestNo' as const },
    { 
      header: t("Request Type"), 
      key:'requestType' as const,
      type:'badge',
      badgeConfig: { getVariant: () =>'secondary' }
    },
    { 
      header: t("Status"), 
      key:'statusDisplay' as const,
      type:'badge',
      badgeConfig: {
        getVariant: (value: string) => {
          if (value ==='Completed') return'success';
          if (value ==='In Progress') return'info';
          return'warning';
        }
      }
    },
    { 
      header: t("Created On"), 
      key:'createdDate' as const,
      render: (val: string, row: any) => (
        <div className="flex flex-col">
          <span className="font-medium text-slate-800">{val}</span>
          <span className="text-[11px] text-slate-500">{t("By {{name}}", { name: row.createdUserRemoteName })}</span>
        </div>
      )
    },
    { 
      header: t("Closed Date"), 
      key:'closeDate' as const,
      render: (val: string) => (val === '-' || !val) ? <span className="text-slate-400 italic text-sm">{t("Ongoing")}</span> : <span className="font-medium text-slate-800">{val}</span>
    },
    {
      header: t("Actions"),
      key: 'actions' as const,
      render: (_, row: any) => (
        <Button variant="outline" onClick={() => setSelectedRequest(row)} className="h-7 px-2.5 text-xs rounded-md font-semibold flex items-center gap-1 border-gray-200 text-gray-700">
           <Eye className="w-3.5 h-3.5"/> {t("View")}
        </Button>
      )
    }
  ], [t]);

  const totalItems = requestData?.count || 0;

  return (
    <div className="mt-4">
        <GenericTable
          data={requestData?.results}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          selectable={false}
          pagination={{
            currentPage: page,
            totalPages: Math.ceil(totalItems / limit) || 1,
            totalItems: totalItems,
            startItem: totalItems === 0 ? 0 : (page - 1) * limit + 1,
            endItem: Math.min(page * limit, totalItems)
          }}
          onPageChange={setPage}
          searchPlaceholder={t("Search requests...")}
        />
      <RequestDetailsModal 
        isOpen={!!selectedRequest} 
        onClose={() => setSelectedRequest(null)} 
        requestData={selectedRequest} 
      />
    </div>
  );
}
